import { Controller } from "stimulus";

// Makes the tickets detail side-panel horizontally resizable. Dragging the handle
// on the panel's left border widens or narrows it, clamped between a minimum width
// (in px) and a maximum expressed as a fraction of the viewport width (vw). The
// chosen width is persisted in localStorage so it survives reloads and ticket
// selections.
//
// The panel is docked to the right edge of a flex row, so its width equals the
// distance from the pointer to the viewport's right edge while dragging.
export default class extends Controller {
  static targets = ["panel", "handle"];
  static values = {
    min: { type: Number, default: 360 },
    maxVw: { type: Number, default: 50 },
    storageKey: { type: String, default: "ticketsDetailPanelWidth" }
  };

  connect() {
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.restoreWidth();
  }

  disconnect() {
    this.stopDragging();
  }

  start(event) {
    event.preventDefault();
    this.dragging = true;
    document.addEventListener("mousemove", this.onMouseMove);
    document.addEventListener("mouseup", this.onMouseUp);
    document.body.classList.add("select-none");
    this.handleTarget.classList.add("bg-indigo-400");
  }

  onMouseMove(event) {
    if (!this.dragging) return;
    this.applyWidth(window.innerWidth - event.clientX);
  }

  onMouseUp() {
    this.stopDragging();
  }

  stopDragging() {
    if (!this.dragging) return;
    this.dragging = false;
    document.removeEventListener("mousemove", this.onMouseMove);
    document.removeEventListener("mouseup", this.onMouseUp);
    document.body.classList.remove("select-none");
    this.handleTarget.classList.remove("bg-indigo-400");
    this.persistWidth();
  }

  // Clamp between the min width and the max fraction of the viewport (so the list
  // keeps room on narrower screens), then apply it as an inline width on the panel.
  applyWidth(width) {
    const upperBound = window.innerWidth * (this.maxVwValue / 100);
    const clamped = Math.min(upperBound, Math.max(this.minValue, width));
    this.currentWidth = clamped;
    this.panelTarget.style.width = `${clamped}px`;
  }

  restoreWidth() {
    const stored = this.readStoredWidth();
    if (stored) this.applyWidth(stored);
  }

  persistWidth() {
    try {
      window.localStorage.setItem(this.storageKeyValue, String(this.currentWidth));
    } catch (_error) {
      // Ignore storage failures (private mode, quota): resize still works this session.
    }
  }

  readStoredWidth() {
    try {
      const value = Number(window.localStorage.getItem(this.storageKeyValue));
      return Number.isFinite(value) && value > 0 ? value : null;
    } catch (_error) {
      return null;
    }
  }
}
